import { TraceEvent } from '@app/telemetry/trace-event.interface'

export const AUTO_TITLE_MAX = 160
const SUMMARY_MAX_WORDS = 9

//* Palabras con las que un título nunca debe terminar.
const TRAILING_STOPWORDS = new Set(['la', 'el', 'los', 'las', 'un', 'una', 'unos', 'unas', 'de', 'del', 'y', 'e', 'o', 'u', 'a', 'en', 'con', 'sin', 'por', 'para', 'que', 'al', 'su', 'sus', 'mi', 'mis', 'lo', 'me', 'te', 'se', 'nos', 'le', 'les', 'no', 'ni', 'ya', 'muy', 'más'])

//* Muletillas con las que arranca casi cualquier reporte hablado. Quitarlas convierte
//* "Pagos: estaba en pagos y no me dejó guardar" en "Pagos: no me dejó guardar".
const FILLERS = /^(hola|buenas|oigan|oye|estaba en|estoy en|estaba|estoy|cuando|al |le di|di |quise|intenté|intente|traté|trate)\b[\s,:]*/i

//* Dos mapas literales en vez de una plantilla libre: el título tiene que salir IGUAL cada vez para
//* el mismo fallo, porque es lo que permite ver de un vistazo que tres reportes son el mismo problema.
const VERB_BY_METHOD: Record<string, string> = {
  POST: 'no se guardó',
  PATCH: 'no se guardó',
  PUT: 'no se guardó',
  DELETE: 'no se pudo borrar',
  GET: 'no cargó',
}

const CAUSE_BY_STATUS: Array<{ test: (s: number) => boolean; text: string }> = [
  { test: (s) => s === 0, text: 'sin respuesta del servidor' },
  { test: (s) => s === 400, text: 'datos rechazados' },
  { test: (s) => s === 403, text: 'sin permisos' },
  { test: (s) => s === 404, text: 'no se encontró' },
  { test: (s) => s === 409, text: 'conflicto al guardar' },
  { test: (s) => s === 422, text: 'datos inválidos' },
  { test: (s) => s >= 500, text: 'error del servidor' },
  { test: (s) => s >= 400, text: 'la petición falló' },
]

function flatten(text: string): string {
  return (text ?? '').replace(/\s+/g, ' ').trim()
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function truncate(text: string, max = AUTO_TITLE_MAX): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max - 3)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

/**
 * Primera oración de la descripción, sin muletillas ni el nombre de la pantalla.
 *
 * Se le quita la pantalla porque la frase típica es "estaba en pagos y no me dejó guardar" y el
 * título ya empieza con "Pagos:": dejarla daría "Pagos: pagos y no me dejó guardar".
 */
function summarizeDescription(description: string, screen?: string): string {
  const flat = flatten(description)
  if (!flat) return ''
  const firstSentence = flat.split(/(?<=[.!?])\s/)[0] ?? flat

  let text = firstSentence.replace(FILLERS, '').trim()
  const screenName = flatten(screen ?? '')
  if (screenName) {
    const redundant = new RegExp(`^(la |el |mi |de )?${escapeRegExp(screenName)}\\b[\\s,:]*(y|e|pero|que)?[\\s,:]*`, 'i')
    const stripped = text.replace(redundant, '').trim()
    //* Solo si queda frase: si lo único que escribieron fue el nombre de la pantalla, quitarlo
    //* dejaría el título sin contenido.
    if (stripped) text = stripped
  }

  const words = (text || firstSentence).split(' ').slice(0, SUMMARY_MAX_WORDS)
  //* Si el corte por número de palabras deja colgando un artículo o una preposición ("…por RFC la"),
  //* se quitan: el título se lee mal y no aporta nada.
  while (words.length > 1 && TRAILING_STOPWORDS.has(words[words.length - 1].toLowerCase())) words.pop()
  return words.join(' ').replace(/[.,;:]$/, '')
}

function summarizeFailure(failure: TraceEvent): string {
  if (failure.kind === 'error') return 'error en pantalla'
  const method = String(failure.data?.['method'] ?? 'GET').toUpperCase()
  const status = Number(failure.data?.['status'] ?? 0)
  const verb = VERB_BY_METHOD[method] ?? 'falló'
  const cause = CAUSE_BY_STATUS.find((c) => c.test(status))?.text
  return cause ? `${verb} (${cause})` : verb
}

export interface AutoTitleInput {
  screenLabel: string
  description: string
  lastFailure: TraceEvent | null
}

/**
 * Compone el título del reporte: `<Pantalla>: <qué salió mal>`.
 *
 * Determinista y sin IA. El resumen sale, por orden de calidad, del último fallo de la bitácora
 * —que es lo más preciso que hay—, o de las primeras palabras de lo que escribió la persona. Nunca
 * queda vacío: un reporte sin título es un reporte que nadie abre.
 */
export function buildAutoTitle({ screenLabel, description, lastFailure }: AutoTitleInput): string {
  const prefix = flatten(screenLabel ?? '')
  const summary = (lastFailure ? summarizeFailure(lastFailure) : '') || summarizeDescription(description, prefix) || 'problema reportado'
  const title = prefix ? `${prefix}: ${summary}` : summary.charAt(0).toUpperCase() + summary.slice(1)
  return truncate(title)
}
